import { useState, useEffect, useCallback } from 'react';
import { useMatrixClient } from '../context/MatrixContext';
import { RoomEvent } from '../constants/matrixEvents';

/**
 * Hook to get the read status for all messages in a room.
 * Returns a map from eventId to a Set of user IDs who have read that message.
 * Used for showing read indicators (checkmarks) on sent messages.
 *
 * @param {import('matrix-js-sdk').Room} room - The room object from the matrix-js-sdk.
 * @returns {Map<string, Set<string>>} A map where keys are event IDs and values are Sets of user IDs who read the message.
 */
export const useRoomReadReceipts = (room) => {
  const client = useMatrixClient();
  const [readReceipts, setReadReceipts] = useState(new Map());

  const calculateAllReadReceipts = useCallback(() => {
    if (!client || !room) {
      setReadReceipts(new Map());
      return;
    }

    const newReadReceipts = new Map();
    const currentUserId = client.getUserId();
    const members = room.getJoinedMembers();
    const otherMembers = members.filter(m => m.userId !== currentUserId);

    // Iterate through all events in the live timeline
    const events = room.getLiveTimeline().getEvents();
    for (const event of events) {
      // We only care about read status for our own messages (for the checkmark indicator)
      if (event.getSender() !== currentUserId) {
        continue;
      }

      const eventId = event.getId();
      const readByUsers = new Set();
      
      for (const member of otherMembers) {
        if (room.hasUserReadEvent(member.userId, eventId)) {
          readByUsers.add(member.userId);
        }
      }
      
      newReadReceipts.set(eventId, readByUsers);
    }

    setReadReceipts(newReadReceipts);
  }, [client, room]);

  useEffect(() => {
    if (!room) return;

    calculateAllReadReceipts();

    // A receipt event means the read-up-to marker has changed for a user.
    room.on(RoomEvent.Receipt, calculateAllReadReceipts);

    return () => {
      room.off(RoomEvent.Receipt, calculateAllReadReceipts);
    };
  }, [room, calculateAllReadReceipts]);

  return readReceipts;
};

export default useRoomReadReceipts;
